'use client';
import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import { toast } from 'sonner';

export interface LinkedTicket {
  id: string;
  subject: string;
  status: string;
}

interface Props {
  channel: string;
  citizen: string;
  citizenFull: string;
  duration: string;
  linkedTicket: LinkedTicket | null;
  mirRef: string;
  onConfirmResolve: (ticketId: string) => void;
  onKeepOpen: () => void;
  onEscalate: () => void;
  onDiscard: () => void;
}

const channelWrapUpLabel: Record<string, string> = {
  'LIVE CHAT': 'Live Chat',
  'WHATSAPP': 'WhatsApp',
  'EMAIL': 'Email',
  'X': 'X',
  'FACEBOOK': 'Facebook',
  'INSTAGRAM': 'Instagram',
};

function getChannelDefaults(channel: string, citizen: string, linkedTicket: LinkedTicket | null) {
  if (channel === 'WHATSAPP' || channel === 'LIVE CHAT') {
    return {
      subject: linkedTicket?.subject || 'Passport Application Status Enquiry',
      agency: 'Jabatan Imigresen Malaysia',
      category: 'Passport',
      subcategory: 'Application Status',
      disposition: 'Information Provided',
      priority: 'Medium',
      status: 'Resolved',
      followUp: 'Not Required',
      issue: 'Citizen requested clarification on the status and requirements related to a passport application/renewal.',
      summary: `Citizen contacted MyGCC through ${channelWrapUpLabel[channel] || channel} regarding passport application status and renewal requirements. The agent provided the relevant documentation requirements and explained the next steps. Citizen acknowledged the information provided.`,
      actionTaken: 'Passport renewal/application requirements explained. Relevant verified Knowledge Base information provided.',
    };
  }
  if (channel === 'EMAIL') {
    return {
      subject: linkedTicket?.subject || 'STR Application Status Enquiry',
      agency: 'KPKT',
      category: 'Government Assistance',
      subcategory: 'STR Application Status',
      disposition: 'Information Provided',
      priority: 'Medium',
      status: 'Resolved',
      followUp: 'Not Required',
      issue: 'Citizen requested status update on STR application submitted previously.',
      summary: 'Citizen emailed MyGCC requesting the current status of an STR application. Agent reviewed the case and provided a status update with next steps.',
      actionTaken: 'STR application status reviewed. Citizen informed of current status and expected timeline.',
    };
  }
  return {
    subject: linkedTicket?.subject || `${citizen} Enquiry`,
    agency: 'MyGCC',
    category: 'General Enquiry',
    subcategory: 'Application Status',
    disposition: 'Information Provided',
    priority: 'Medium',
    status: 'Resolved',
    followUp: 'Not Required',
    issue: `Citizen contacted MyGCC via ${channelWrapUpLabel[channel] || channel} with an enquiry.`,
    summary: `Citizen contacted MyGCC through ${channelWrapUpLabel[channel] || channel}. Agent provided relevant information and guidance.`,
    actionTaken: 'Relevant information provided. Issue resolved at first contact.',
  };
}

export default function NonVoiceAIWrapUp({
  channel, citizen, citizenFull, duration, linkedTicket, mirRef,
  onConfirmResolve, onKeepOpen, onEscalate, onDiscard,
}: Props) {
  const [preparing, setPreparing] = useState(true);
  const [editedFields, setEditedFields] = useState<Set<string>>(new Set());
  const [showFollowUp, setShowFollowUp] = useState(false);
  const [followUpDate, setFollowUpDate] = useState('');
  const [followUpNotes, setFollowUpNotes] = useState('');
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successTicketId, setSuccessTicketId] = useState('');

  const defaults = getChannelDefaults(channel, citizen, linkedTicket);
  const [fields, setFields] = useState(defaults);
  const [summary, setSummary] = useState(defaults.summary);

  useEffect(() => {
    const t = setTimeout(() => setPreparing(false), 2200);
    return () => clearTimeout(t);
  }, []);

  const updateField = (key: string, value: string) => {
    setFields(prev => ({ ...prev, [key]: value }));
    setEditedFields(prev => new Set(prev).add(key));
  };

  const handleConfirmResolve = () => {
    const ticketId = linkedTicket?.id || `MYGCC-TKT-2026-${Math.floor(10000 + Math.random() * 90000)}`;
    setSuccessTicketId(ticketId);
    setShowSuccess(true);
  };

  const handleDone = () => {
    onConfirmResolve(successTicketId);
  };

  if (preparing) {
    return (
      <div className="p-6 flex flex-col items-center justify-center h-full space-y-4">
        <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center mb-2">
          <Icon name="SparklesIcon" size={22} className="text-primary" />
        </div>
        <div className="flex gap-1.5 mb-1">
          {[0, 1, 2].map((i) => (
            <span key={i} className="w-2.5 h-2.5 bg-primary rounded-full ai-listening-dot" style={{ animationDelay: `${i * 0.16}s` }} />
          ))}
        </div>
        <div className="text-[14px] font-700 text-primary">AI Assisted Wrap-Up</div>
        <div className="text-[12px] text-muted-foreground text-center max-w-[240px]">
          CX AI Assist is preparing the interaction summary and {linkedTicket ? 'ticket update' : 'ticket draft'} for your review.
        </div>
      </div>
    );
  }

  if (showSuccess) {
    return (
      <div className="p-6 flex flex-col items-center justify-center h-full space-y-4">
        <div className="w-14 h-14 rounded-2xl bg-green-50 border border-green-200 flex items-center justify-center mb-2">
          <Icon name="CheckCircleIcon" size={28} className="text-green-600" />
        </div>
        <div className="text-[16px] font-700 text-foreground">Interaction Completed</div>
        <div className="w-full space-y-2 text-[12px]">
          {[
            { label: 'Ticket', value: successTicketId, mono: true },
            { label: 'Status', value: 'Resolved' },
            { label: 'Citizen', value: citizenFull },
            { label: 'Channel', value: channelWrapUpLabel[channel] || channel },
            { label: 'MIR Screen Recording', value: mirRef, mono: true },
          ].map(row => (
            <div key={row.label} className="flex items-center justify-between bg-muted/40 rounded-xl px-3 py-2">
              <span className="text-muted-foreground">{row.label}</span>
              <span className={`font-600 text-foreground ${row.mono ? 'font-mono text-[11px]' : ''}`}>{row.value}</span>
            </div>
          ))}
        </div>
        <div className="w-full space-y-1.5 text-[11px] text-muted-foreground">
          <div className="flex items-center gap-2"><Icon name="CheckCircleIcon" size={12} className="text-green-500" />Customer Journey Updated</div>
          <div className="flex items-center gap-2"><Icon name="CheckCircleIcon" size={12} className="text-green-500" />Ticket Updated</div>
          <div className="flex items-center gap-2"><Icon name="CheckCircleIcon" size={12} className="text-green-500" />Screen Recording Archived</div>
        </div>
        <button onClick={handleDone} className="w-full btn-primary py-3 text-[14px] font-700 flex items-center justify-center gap-2 mt-2">
          <Icon name="CheckIcon" size={16} />
          Done
        </button>
      </div>
    );
  }

  if (showFollowUp) {
    return (
      <div className="p-4 space-y-4">
        <div className="flex items-center gap-2 mb-2">
          <button onClick={() => setShowFollowUp(false)} className="p-1.5 rounded-lg hover:bg-muted transition-colors">
            <Icon name="ArrowLeftIcon" size={14} className="text-muted-foreground" />
          </button>
          <h3 className="text-[14px] font-700 text-foreground">Keep Open & Follow-Up</h3>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-[11px] text-amber-800">
          <strong>Note:</strong> The interaction will be completed but the Ticket will remain open with Pending Follow-Up status.
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-[11px] font-700 text-muted-foreground mb-1.5">Follow-Up Required</label>
            <div className="input-field bg-green-50 text-green-700 font-600 text-[12px]">Yes</div>
          </div>
          <div>
            <label className="block text-[11px] font-700 text-muted-foreground mb-1.5">Follow-Up Date</label>
            <input type="date" value={followUpDate} onChange={e => setFollowUpDate(e.target.value)} className="input-field w-full" />
          </div>
          <div>
            <label className="block text-[11px] font-700 text-muted-foreground mb-1.5">Assigned To</label>
            <select className="input-field w-full">
              <option>Nur Aisyah</option>
              <option>Supervisor</option>
              <option>Agency Officer</option>
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-700 text-muted-foreground mb-1.5">Notes</label>
            <textarea rows={3} value={followUpNotes} onChange={e => setFollowUpNotes(e.target.value)} placeholder="Follow-up notes..." className="input-field w-full resize-none" />
          </div>
        </div>
        <div className="text-[11px] text-muted-foreground bg-muted/40 rounded-xl px-3 py-2">
          Ticket status will become: <strong className="text-amber-700">Pending Follow-Up</strong>
        </div>
        <button onClick={() => {
          toast.success('Follow-up scheduled', { description: `Ticket set to Pending Follow-Up${followUpDate ? ' · ' + followUpDate : ''}` });
          onKeepOpen();
        }} className="w-full btn-primary py-2.5 text-[13px] flex items-center justify-center gap-2">
          <Icon name="CalendarIcon" size={14} />
          Confirm Follow-Up
        </button>
      </div>
    );
  }

  if (showDiscardConfirm) {
    return (
      <div className="p-6 flex flex-col items-center justify-center h-full space-y-4">
        <div className="w-12 h-12 rounded-2xl bg-red-50 flex items-center justify-center">
          <Icon name="TrashIcon" size={22} className="text-red-500" />
        </div>
        <div className="text-[14px] font-700 text-foreground text-center">Discard AI Draft?</div>
        <div className="text-[12px] text-muted-foreground text-center">
          The AI-generated wrap-up draft will be discarded. The interaction itself will not be affected.
        </div>
        <div className="flex gap-3 w-full">
          <button onClick={() => setShowDiscardConfirm(false)} className="flex-1 btn-secondary py-2.5 text-[13px]">Cancel</button>
          <button onClick={() => { onDiscard(); toast.info('AI draft discarded'); }} className="flex-1 py-2.5 text-[13px] bg-red-600 hover:bg-red-700 text-white rounded-xl font-600 transition-colors">
            Discard Draft
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-3 overflow-y-auto scrollbar-thin">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Icon name="SparklesIcon" size={15} className="text-primary" />
        <h2 className="text-[13px] font-700 text-primary">AI Assisted Wrap-Up</h2>
        <span className="ai-badge">AI</span>
      </div>

      {/* Review banner */}
      <div className="bg-amber-50 border border-amber-300 rounded-xl p-2.5 flex items-start gap-2">
        <Icon name="ExclamationTriangleIcon" size={13} className="text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="text-[11px] text-amber-800">
          <span className="font-700">Generated by CX AI Assist · Agent Review Required</span>
          <br />Review all fields before finalising the interaction and Ticket.
        </div>
      </div>

      {/* Ticket context */}
      {linkedTicket ? (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
          <div className="flex items-center gap-2 mb-1">
            <Icon name="TicketIcon" size={13} className="text-blue-600" />
            <span className="text-[11px] font-700 text-blue-700">Updating Existing Ticket</span>
          </div>
          <div className="text-[12px] font-700 text-blue-800 font-mono">{linkedTicket.id}</div>
          <div className="text-[11px] text-blue-700">{linkedTicket.subject}</div>
          <div className="text-[10px] text-blue-600 mt-1">AI Assist has prepared updates for the existing Ticket. Subject to agent approval.</div>
        </div>
      ) : (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
          <div className="flex items-center gap-2 mb-1">
            <Icon name="InformationCircleIcon" size={13} className="text-slate-500" />
            <span className="text-[11px] font-700 text-slate-600">New Ticket Draft</span>
          </div>
          <div className="text-[10px] text-slate-600">No Ticket is currently linked to this interaction. CX AI Assist has prepared a new Ticket draft.</div>
        </div>
      )}

      {/* Suggested Ticket Subject */}
      <div className="card-elevated p-3">
        <div className="flex items-center justify-between mb-1.5">
          <div className="text-[10px] font-700 uppercase tracking-wide text-muted-foreground">Suggested Ticket Subject</div>
          {editedFields.has('subject') ? (
            <span className="text-[9px] text-blue-600 font-600 bg-blue-50 px-1.5 py-0.5 rounded">Edited by Agent</span>
          ) : (
            <span className="text-[9px] text-primary font-600 bg-primary/10 px-1.5 py-0.5 rounded">✨ AI Generated</span>
          )}
        </div>
        <input value={fields.subject} onChange={e => updateField('subject', e.target.value)}
          className="w-full text-[13px] font-600 text-foreground border border-border rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-primary" />
      </div>

      {/* Classification */}
      <div className="card-elevated p-3 space-y-2">
        <div className="text-[10px] font-700 uppercase tracking-wide text-muted-foreground">Classification</div>
        {[
          { key: 'agency', label: 'Agency' },
          { key: 'category', label: 'Category' },
          { key: 'subcategory', label: 'Subcategory' },
          { key: 'disposition', label: 'Suggested Disposition' },
          { key: 'priority', label: 'Suggested Priority' },
          { key: 'status', label: 'Suggested Status' },
          { key: 'followUp', label: 'Follow-Up' },
        ].map(f => (
          <div key={f.key} className="flex items-center justify-between gap-2 text-[12px]">
            <span className="text-muted-foreground flex-shrink-0 w-32">{f.label}</span>
            <div className="flex items-center gap-1.5 flex-1 justify-end">
              {editedFields.has(f.key) && (
                <span className="text-[9px] text-blue-600 font-600 bg-blue-50 px-1 py-0.5 rounded flex-shrink-0">Edited</span>
              )}
              <input value={fields[f.key as keyof typeof fields] as string}
                onChange={e => updateField(f.key, e.target.value)}
                className="text-[12px] font-600 text-foreground border border-border rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary text-right w-36" />
            </div>
          </div>
        ))}
      </div>

      {/* Customer Issue */}
      <div className="card-elevated p-3">
        <div className="flex items-center justify-between mb-1.5">
          <div className="text-[10px] font-700 uppercase tracking-wide text-muted-foreground">Customer Issue</div>
          {editedFields.has('issue') ? (
            <span className="text-[9px] text-blue-600 font-600 bg-blue-50 px-1.5 py-0.5 rounded">Edited by Agent</span>
          ) : (
            <span className="text-[9px] text-primary font-600 bg-primary/10 px-1.5 py-0.5 rounded">✨ AI Generated</span>
          )}
        </div>
        <textarea value={fields.issue} onChange={e => updateField('issue', e.target.value)}
          rows={2} className="w-full text-[12px] text-foreground border border-border rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-primary resize-none" />
      </div>

      {/* Interaction Summary */}
      <div className="card-elevated p-3">
        <div className="flex items-center justify-between mb-1.5">
          <div className="text-[10px] font-700 uppercase tracking-wide text-muted-foreground">Interaction Summary</div>
          {editedFields.has('summary') ? (
            <span className="text-[9px] text-blue-600 font-600 bg-blue-50 px-1.5 py-0.5 rounded">Edited by Agent</span>
          ) : (
            <span className="text-[9px] text-primary font-600 bg-primary/10 px-1.5 py-0.5 rounded">✨ AI Generated</span>
          )}
        </div>
        <textarea value={summary} onChange={e => { setSummary(e.target.value); setEditedFields(prev => new Set(prev).add('summary')); }}
          rows={3} className="w-full text-[12px] text-foreground border border-border rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-primary resize-none" />
      </div>

      {/* Action Taken */}
      <div className="card-elevated p-3">
        <div className="flex items-center justify-between mb-1.5">
          <div className="text-[10px] font-700 uppercase tracking-wide text-muted-foreground">Action Taken</div>
          {editedFields.has('actionTaken') ? (
            <span className="text-[9px] text-blue-600 font-600 bg-blue-50 px-1.5 py-0.5 rounded">Edited by Agent</span>
          ) : (
            <span className="text-[9px] text-primary font-600 bg-primary/10 px-1.5 py-0.5 rounded">✨ AI Generated</span>
          )}
        </div>
        <textarea value={fields.actionTaken} onChange={e => updateField('actionTaken', e.target.value)}
          rows={2} className="w-full text-[12px] text-foreground border border-border rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-primary resize-none" />
      </div>

      {/* Sentiment Journey */}
      <div className="card-elevated p-3">
        <div className="text-[10px] font-700 uppercase tracking-wide text-muted-foreground mb-2">Sentiment Journey</div>
        <div className="flex items-center gap-2 text-[11px]">
          <div className="flex flex-col items-center gap-0.5">
            <span className="text-[18px]">😟</span>
            <span className="text-muted-foreground text-[9px]">Initial</span>
            <span className="text-[10px] font-600">Frustrated</span>
          </div>
          <Icon name="ArrowRightIcon" size={10} className="text-muted-foreground" />
          <div className="flex flex-col items-center gap-0.5">
            <span className="text-[18px]">😐</span>
            <span className="text-muted-foreground text-[9px]">During</span>
            <span className="text-[10px] font-600">Neutral</span>
          </div>
          <Icon name="ArrowRightIcon" size={10} className="text-muted-foreground" />
          <div className="flex flex-col items-center gap-0.5">
            <span className="text-[18px]">🙂</span>
            <span className="text-muted-foreground text-[9px]">Final</span>
            <span className="text-[10px] font-600">Satisfied</span>
          </div>
        </div>
      </div>

      {/* MIR Screen Recording Reference */}
      <div className="card-elevated p-3">
        <div className="text-[10px] font-700 uppercase tracking-wide text-muted-foreground mb-2">MIR Screen Recording Reference</div>
        <div className="flex items-center gap-2 mb-1.5">
          <div className="w-6 h-6 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
            <Icon name="ComputerDesktopIcon" size={13} className="text-slate-600" />
          </div>
          <span className="font-700 font-mono text-[12px] text-foreground">{mirRef}</span>
          <span className="ml-auto text-[11px] text-green-600 font-600 bg-green-50 px-1.5 py-0.5 rounded">Completed</span>
        </div>
        <div className="grid grid-cols-2 gap-1.5 text-[11px]">
          <div className="flex justify-between"><span className="text-muted-foreground">Duration:</span><span className="font-600">{duration}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Channel:</span><span className="font-600">{channelWrapUpLabel[channel] || channel}</span></div>
          <div className="flex justify-between col-span-2"><span className="text-muted-foreground">Status:</span><span className="font-600 text-green-600">Screen Recording Completed</span></div>
        </div>
        <div className="text-[10px] text-muted-foreground mt-1.5">Agent interaction screen recording managed by Mitel MIR</div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col gap-2 pt-1">
        <button onClick={handleConfirmResolve}
          className="btn-primary py-2.5 flex items-center justify-center gap-2 text-[13px] font-700">
          <Icon name="CheckCircleIcon" size={15} />
          Confirm & Resolve
        </button>
        <button onClick={() => {
          toast.success(`Ticket ${linkedTicket?.id || 'draft'} saved`, { description: 'Ticket saved. Interaction remains open.' });
        }} className="btn-secondary py-2 text-[12px] flex items-center justify-center gap-1.5">
          <Icon name="ArchiveBoxIcon" size={13} />
          Confirm & Save Ticket
        </button>
        <div className="flex gap-2">
          <button onClick={() => setShowFollowUp(true)}
            className="flex-1 btn-ghost py-2 text-[12px] border border-amber-200 text-amber-700 hover:bg-amber-50 flex items-center justify-center gap-1.5">
            <Icon name="CalendarIcon" size={13} />
            Keep Open & Follow-Up
          </button>
          <button onClick={onEscalate}
            className="flex-1 btn-ghost py-2 text-[12px] border border-border flex items-center justify-center gap-1.5">
            <Icon name="ArrowUpIcon" size={13} />
            Escalate
          </button>
        </div>
        <button onClick={() => setShowDiscardConfirm(true)}
          className="btn-ghost py-2 text-[12px] border border-red-200 text-red-600 hover:bg-red-50 flex items-center justify-center gap-1.5">
          <Icon name="TrashIcon" size={13} />
          Discard AI Draft
        </button>
      </div>

      <div className="text-[10px] text-muted-foreground text-center py-1 border-t border-border pt-2">
        AI-suggested · Agent approval required · Not finalised automatically
      </div>
    </div>
  );
}
